import { Trash2 } from "lucide-react"
import { useDeleteThread } from "@/hooks/useChat"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "@/routes/path"
import { Thread } from "@/types/api"

interface DeleteThreadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  threadToDelete: Thread | null
  currentThreadId?: string | null
}

export function DeleteThreadDialog({ open, onOpenChange, threadToDelete, currentThreadId }: DeleteThreadDialogProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const deleteThreadMutation = useDeleteThread({
    onSuccess: () => {
      // Refresh threads list
      queryClient.invalidateQueries({ queryKey: ["threads"] })

      // Navigate to studio index if the deleted thread was the current one
      if (currentThreadId && threadToDelete && currentThreadId === threadToDelete.id) {
        navigate(ROUTES.DASHBOARD.STUDIO.INDEX)
      }

      onOpenChange(false)
    },
    onError: (error) => {
      console.error("Failed to delete thread:", error)
      // You could add a toast notification here
    },
  })

  const confirmDelete = () => {
    if (threadToDelete) {
      deleteThreadMutation.mutate(threadToDelete.id)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-visible p-0">
        <div className="flex items-start justify-between px-6 pt-6">
          <div className="rounded-lg border border-gray-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900">
            <Trash2 className="h-6 w-6 text-red-500" />
          </div>
        </div>
        <DialogHeader className="px-6 pt-1">
          <DialogTitle className="text-2xl font-semibold">Delete conversation</DialogTitle>
          <DialogDescription className="mt-2">
            Are you sure you want to delete &ldquo;{threadToDelete?.name}&rdquo;? This action cannot be undone and will
            permanently remove all messages in this conversation.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={deleteThreadMutation.isPending}
            size="xs"
            className="text-primary-600 bg-transparent px-3 font-normal shadow-none hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="xs"
            onClick={confirmDelete}
            disabled={deleteThreadMutation.isPending}
            className="min-w-[80px] hover:bg-red-600 dark:hover:bg-red-700"
          >
            {deleteThreadMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
